import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, useToast } from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';

/* ─── Helpers ──────────────────────────────────────── */
function Stars({ rating, count, size = "sm" }) {
  const sz = size === "lg" ? "w-5 h-5" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(n => (
          <svg key={n} className={`${sz} ${n <= Math.round(rating) ? "fill-amber-400" : "fill-stone-200"}`} viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className={`font-semibold text-stone-800 ${size === "lg" ? "text-sm" : "text-xs"}`}>{rating}</span>
      {count !== undefined && (
        <span className={`text-stone-400 ${size === "lg" ? "text-sm" : "text-xs"}`}>({count} reviews)</span>
      )}
    </div>
  );
}

const TAG_STYLES = {
  Premium:    "bg-amber-100 text-amber-700 border-amber-200",
  Bestseller: "bg-rose-100 text-rose-700 border-rose-200",
  Limited:    "bg-purple-100 text-purple-700 border-purple-200",
  New:        "bg-emerald-100 text-emerald-700 border-emerald-200",
  Trending:   "bg-sky-100 text-sky-700 border-sky-200",
};

/* ─── Size Guide Modal ────────────────────────────── */
function SizeGuideModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 z-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-stone-900">Size Guide</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-center border-collapse">
            <thead>
              <tr className="bg-stone-50">
                <th className="px-4 py-3 text-left font-bold text-stone-700 border border-stone-200">Size</th>
                <th className="px-4 py-3 font-bold text-stone-700 border border-stone-200">Chest (in)</th>
                <th className="px-4 py-3 font-bold text-stone-700 border border-stone-200">Waist (in)</th>
                <th className="px-4 py-3 font-bold text-stone-700 border border-stone-200">Hip (in)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["XS", "31–32", "24–25", "34–35"],
                ["S",  "33–34", "26–27", "36–37"],
                ["M",  "35–36", "28–29", "38–39"],
                ["L",  "37–39", "30–32", "40–42"],
                ["XL", "40–42", "33–35", "43–45"],
                ["XXL","43–45", "36–38", "46–48"],
              ].map(([size, chest, waist, hip], i) => (
                <tr key={size} className={i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}>
                  <td className="px-4 py-3 font-semibold text-stone-800 border border-stone-200 text-left">{size}</td>
                  <td className="px-4 py-3 text-stone-600 border border-stone-200">{chest}</td>
                  <td className="px-4 py-3 text-stone-600 border border-stone-200">{waist}</td>
                  <td className="px-4 py-3 text-stone-600 border border-stone-200">{hip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-400 mt-4 text-center">Measurements are in inches. If between sizes, size up.</p>
      </div>
    </div>
  );
}

/* ─── Share Modal ────────────────────────────────── */
function ShareModal({ product, onClose }) {
  const url = window.location.href;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareLinks = [
    {
      name: "WhatsApp",
      color: "bg-green-500 hover:bg-green-600",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      href: `https://wa.me/?text=${encodeURIComponent(`Check out ${product.name} - ${url}`)}`,
    },
    {
      name: "Facebook",
      color: "bg-blue-600 hover:bg-blue-700",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      name: "Twitter",
      color: "bg-stone-900 hover:bg-stone-700",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${product.name}`)}&url=${encodeURIComponent(url)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 z-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-stone-900">Share this product</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex gap-3 mb-6 justify-center">
          {shareLinks.map(link => (
            <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer"
              className={`flex flex-col items-center gap-2 px-4 py-3 rounded-2xl text-white transition-all duration-150 hover:scale-105 ${link.color}`}>
              {link.icon}
              <span className="text-xs font-semibold">{link.name}</span>
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-4 py-3 border border-stone-200">
          <span className="text-xs text-stone-500 truncate flex-1">{url}</span>
          <button onClick={copy} className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${copied ? "bg-emerald-500 text-white" : "bg-stone-900 text-white hover:bg-rose-500"}`}>
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Related Product Card ───────────────────────── */
function RelatedCard({ product }) {
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { addToast } = useToast();
  const inCart = isInCart(product.id);
  const imgSrc = Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : product.img || "";
  const displayPrice = product.sale_price || product.price;

  return (
    <div
      onClick={() => { navigate(`/product/${product.id}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
      className="group bg-white rounded-2xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-stone-100">
        <img src={imgSrc} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {product.tag && (
          <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border ${TAG_STYLES[product.tag] ?? "bg-stone-100 text-stone-600 border-stone-200"}`}>
            {product.tag}
          </span>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-400 mb-0.5">{product.category}</p>
        <h4 className="text-sm font-semibold text-stone-900 leading-snug mb-1 line-clamp-2">{product.name}</h4>
        <Stars rating={product.rating} />
        <div className="flex items-center justify-between mt-auto pt-2">
          <div>
            <span className="text-sm font-bold text-stone-900">${displayPrice}</span>
            {product.sale_price && product.price > product.sale_price && (
              <span className="text-xs text-stone-400 line-through ml-1">${product.price}</span>
            )}
          </div>
          <button
            onClick={e => {
              e.stopPropagation();
              addToCart(product.id, 1);
              addToast(`${product.name} added to cart`);
            }}
            className={`p-2 rounded-full transition-all duration-150 active:scale-95 ${inCart ? "bg-emerald-500 text-white" : "bg-stone-900 text-white hover:bg-rose-500"}`}
          >
            {inCart ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /></svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PRODUCT DETAIL ─────────────────────────── */
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { requireAuth } = useAuth();
  const { addToast } = useToast();

  const [product,       setProduct]       = useState(null);
  const [related,       setRelated]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeImg,     setActiveImg]     = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize,  setSelectedSize]  = useState(null);
  const [qty,           setQty]           = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showShare,     setShowShare]     = useState(false);
  const [wishlisted,    setWishlisted]    = useState(false);
  const [addedAnim,     setAddedAnim]     = useState(false);
  const [showLightbox,  setShowLightbox]  = useState(false);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isSwiping = useRef(false);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    window.scrollTo({ top: 0, behavior: "smooth" });

    fetch(`${API}/api/products/${id}`)
      .then(r => r.json())
      .then(data => {
        setProduct(data);
        setSelectedColor(data.colors?.[0] || null);
        setSelectedSize(data.sizes?.[0] || null);
        setQty(1);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id,API]);

  useEffect(() => {
    setActiveImg(0);
  }, [selectedColor]);

  useEffect(() => {
    if (!id) return;
    fetch(`${API}/api/products/${id}/related`)
      .then(r => r.json())
      .then(data => setRelated(Array.isArray(data) ? data : []))
      .catch(() => setRelated([]));
  }, [id,API]);

  const inCart = product ? isInCart(product.id, selectedColor?.name || null, selectedSize || null) : false;

  const handleAddToCart = useCallback(() => {
  requireAuth(() => {
    addToCart(product.id, qty, selectedColor?.name || null, selectedSize || null);
    addToast(`${product.name} added to cart 🛍️`);
    setAddedAnim(true);
    setTimeout(() => setAddedAnim(false), 1500);
  });
}, [product, qty, selectedColor, selectedSize, addToCart, addToast, requireAuth]);

  const handleBuyNow = useCallback(() => {
  requireAuth(async () => {
    try {
      // This now waits until the cart actually has the product
      await addToCart(product.id, qty, selectedColor?.name || null, selectedSize || null);
      
      // Navigate to checkout
      navigate("/checkout");
      
      // Optional fallback: if React Router fails, force redirect after a delay
      setTimeout(() => {
        if (window.location.pathname !== "/checkout") {
          window.location.href = "/checkout";
        }
      }, 300);
    } catch (error) {
      console.error("Buy Now error:", error);
      addToast("Failed to add to cart. Please try again.");
    }
  });
}, [product, qty, selectedColor, selectedSize, addToCart, requireAuth, navigate, addToast]);


  const discount = product?.sale_price && product?.price > product?.sale_price
    ? Math.round(((product.price - product.sale_price) / product.price) * 100)
    : null;

  const images = Array.isArray(selectedColor?.images) && selectedColor.images.length > 0
    ? selectedColor.images
    : Array.isArray(product?.images) && product.images.length > 0
    ? product.images
    : [product?.img || ""];

  const handleTouchStart = (e) => {
    const touch = e.targetTouches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
    isSwiping.current = false;
  };

  const handleTouchMove = (e) => { /* we only need end */ };

  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const elapsed = Date.now() - touchStartTime.current;

    if (distance > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      isSwiping.current = true;
      if (deltaX < 0) {
        setActiveImg((prev) => (prev + 1) % images.length);
      } else {
        setActiveImg((prev) => (prev - 1 + images.length) % images.length);
      }
    } else if (distance < 15 && elapsed < 300) {
      isSwiping.current = false;
      setShowLightbox(true);
    }
  };

  const handleImageClick = () => {
    if (!isSwiping.current) {
      setShowLightbox(true);
    }
  };

  // Lightbox swipe refs
  const lbTouchStartX = useRef(0);
  const lbTouchEndX = useRef(0);

  const handleLbTouchStart = (e) => {
    lbTouchStartX.current = e.targetTouches[0].clientX;
  };

  const handleLbTouchMove = (e) => {
    lbTouchEndX.current = e.targetTouches[0].clientX;
  };

  const handleLbTouchEnd = () => {
    const diff = lbTouchStartX.current - lbTouchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setActiveImg((prev) => (prev + 1) % images.length);
      } else {
        setActiveImg((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-stone-200 border-t-rose-400 rounded-full animate-spin" />
        <p className="text-sm text-stone-400 font-medium">Loading product…</p>
      </div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="text-center">
        <p className="text-xl font-bold text-stone-800 mb-2">Product not found</p>
        <button onClick={() => navigate("/shop")} className="text-sm text-rose-500 hover:underline">← Back to Shop</button>
      </div>
    </div>
  );

  return (
    <>
      {/* ─── LIGHTBOX ─── */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </>
          )}

          {/* Image – scaled to fit without cropping */}
          <img
            src={images[activeImg]}
            alt={product.name}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleLbTouchStart}
            onTouchMove={handleLbTouchMove}
            onTouchEnd={handleLbTouchEnd}
          />

          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setActiveImg(i); }}
                  className={`w-2 h-2 rounded-full transition-all ${activeImg === i ? "bg-white scale-125" : "bg-white/40"}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <Helmet>
        <title>{product.seo_title || `${product.name} — Sagar Fashion`}</title>
        <meta name="description" content={product.seo_description || product.description || "Shop this product at Sagar Fashion."} />
        <meta name="keywords" content={product.seo_keywords || `${product.name}, ${product.category}, sagar fashion`} />
        <meta property="og:title" content={product.seo_title || product.name} />
        <meta property="og:description" content={product.seo_description || product.description || ""} />
        {product.images?.[0] && (
          <meta property="og:image" content={
            typeof product.images === "string"
              ? JSON.parse(product.images)[0]
              : product.images[0]
          } />
        )}
      </Helmet>

      {showSizeGuide && <SizeGuideModal onClose={() => setShowSizeGuide(false)} />}
      {showShare && <ShareModal product={product} onClose={() => setShowShare(false)} />}

      <div className="min-h-screen bg-stone-50 pt-20">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-4">
          <nav className="flex items-center gap-2 text-xs text-stone-400">
            <button onClick={() => navigate("/")} className="hover:text-stone-700 transition-colors">Home</button>
            <span>/</span>
            <button onClick={() => navigate("/shop")} className="hover:text-stone-700 transition-colors">Shop</button>
            <span>/</span>
            <span className="text-stone-600 font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 md:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">
            <div className="flex flex-col gap-4">
              <div
                className="relative aspect-square rounded-3xl overflow-hidden bg-stone-100 shadow-md cursor-zoom-in"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={handleImageClick}
              >
                <img
                  key={activeImg}
                  src={images[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
                {product.tag && (
                  <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide border ${TAG_STYLES[product.tag] ?? "bg-stone-100 text-stone-600 border-stone-200"}`}>
                    {product.tag}
                  </span>
                )}
                {discount && (
                  <span className="absolute top-4 right-4 bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    -{discount}%
                  </span>
                )}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveImg(i => (i - 1 + images.length) % images.length);
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm shadow-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveImg(i => (i + 1) % images.length);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm shadow-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-150 ${activeImg === i ? "border-stone-900 shadow-md scale-105" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={img} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right column – details unchanged */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold tracking-widest uppercase text-stone-400">
                  {product.category}{product.subcategory ? ` › ${product.subcategory}` : ""}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowShare(true)}
                    className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center text-stone-500 hover:text-rose-500 hover:border-rose-300 transition-all shadow-sm" title="Share">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  </button>
                  <button
                    onClick={() => { setWishlisted(w => !w); addToast(wishlisted ? "Removed from wishlist" : "Added to wishlist ❤️"); }}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-sm border ${wishlisted ? "bg-rose-500 text-white border-rose-500" : "bg-white border-stone-200 text-stone-400 hover:text-rose-500 hover:border-rose-300"}`}
                    title="Wishlist">
                    <svg className="w-4 h-4" fill={wishlisted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                  </button>
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-stone-900 leading-tight">{product.name}</h1>
              <Stars rating={product.rating} count={product.rating_count} size="lg" />

              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-3xl font-bold text-stone-900">${product.sale_price || product.price}</span>
                {product.sale_price && product.price > product.sale_price && (
                  <>
                    <span className="text-lg text-stone-400 line-through">${product.price}</span>
                    <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">Save {discount}%</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {product.stock > 10 ? (
                  <><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /><span className="text-sm text-emerald-600 font-medium">In Stock</span></>
                ) : product.stock > 0 ? (
                  <><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /><span className="text-sm text-amber-600 font-medium">Only {product.stock} left!</span></>
                ) : (
                  <><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /><span className="text-sm text-red-500 font-medium">Out of Stock</span></>
                )}
              </div>

              <div className="text-xs text-stone-400 flex items-center gap-1.5 mt-1">
  <svg className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
  Need help?{" "}
  <a href="mailto:support@sagarfashion.com" className="underline hover:text-rose-500 transition-colors">sagarfashion777@gmail.com
</a>
  {" or "}
  <a href="tel:+1234567890" className="underline hover:text-rose-500 transition-colors">+1 (718) 630-1100</a>
</div>

              <div className="h-px bg-stone-100" />

              {product.colors?.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-stone-800 mb-2.5">
                    Color: <span className="font-normal text-stone-500 capitalize">{selectedColor?.name}</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map(color => (
                      <button key={color.name} onClick={() => setSelectedColor(color)} title={color.name}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-sm font-medium transition-all duration-150 ${selectedColor?.name === color.name ? "border-stone-900 shadow-md scale-105" : "border-stone-200 hover:border-stone-400"}`}>
                        <span className="w-4 h-4 rounded-full border border-stone-200 flex-shrink-0" style={{ backgroundColor: color.hex }} />
                        <span className="capitalize text-stone-700">{color.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.sizes?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="text-sm font-bold text-stone-800">
                      Size: <span className="font-normal text-stone-500">{selectedSize}</span>
                    </p>
                    <button onClick={() => setShowSizeGuide(true)}
                      className="text-xs text-rose-500 hover:text-rose-700 font-semibold underline underline-offset-2 transition-colors">
                      Size Guide
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map(size => (
                      <button key={size} onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all duration-150 ${selectedSize === size ? "bg-stone-900 text-white border-stone-900 shadow-md" : "border-stone-200 text-stone-600 hover:border-stone-400 bg-white"}`}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-bold text-stone-800 mb-2.5">Quantity</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border-2 border-stone-200 rounded-xl overflow-hidden">
                    <button onClick={() => setQty(q => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors font-bold text-lg">−</button>
                    <span className="w-12 text-center font-bold text-stone-900">{qty}</span>
                    <button onClick={() => setQty(q => Math.min(product.stock || 99, q + 1))}
                      className="w-10 h-10 flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors font-bold text-lg">+</button>
                  </div>
                  <span className="text-sm text-stone-400">{product.stock} available</span>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={handleAddToCart} disabled={product.stock === 0}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg active:scale-95 ${
                    product.stock === 0 ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                    : addedAnim ? "bg-emerald-500 text-white"
                    : inCart   ? "bg-stone-700 text-white hover:bg-stone-800"
                    : "bg-stone-900 text-white hover:bg-rose-500 hover:shadow-rose-200"
                  }`}>
                  {addedAnim ? (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Added!</>
                  ) : inCart ? (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /></svg>Add More</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /></svg>Add to Cart</>
                  )}
                </button>
                <button onClick={handleBuyNow} disabled={product.stock === 0}
                  className="flex-1 py-3.5 rounded-full font-bold text-sm bg-rose-500 text-white hover:bg-rose-600 transition-all duration-300 hover:shadow-lg hover:shadow-rose-200 active:scale-95 disabled:bg-stone-200 disabled:text-stone-400 disabled:cursor-not-allowed">
                  Buy Now
                </button>
              </div>

              <div className="h-px bg-stone-100" />

              {(product.description || product.fabric || product.sleeve || product.pattern || product.collar) && (
                <div className="space-y-4">
                  {product.description && (
                    <div>
                      <h3 className="text-sm font-bold text-stone-800 mb-1.5">Description</h3>
                      <p className="text-sm text-stone-500 leading-relaxed">{product.description}</p>
                    </div>
                  )}
                  {(product.fabric || product.sleeve || product.pattern || product.collar) && (
                    <div>
                      <h3 className="text-sm font-bold text-stone-800 mb-2.5">Product Details</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {[["Fabric", product.fabric], ["Sleeve", product.sleeve], ["Pattern", product.pattern], ["Collar", product.collar]]
                          .filter(([, v]) => v)
                          .map(([label, value]) => (
                            <div key={label} className="bg-stone-50 rounded-xl px-3 py-2.5">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-0.5">{label}</p>
                              <p className="text-sm font-semibold text-stone-700">{value}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
  {[
    {
      icon: "🚚",
      label: "Free Delivery",
      sub: "Orders over $100",
      link: "/ShippingPolicy"          // ← new
    },
    {
      icon: "↩️",
      label: "Easy Returns",
      sub: "30-day policy · Free size exchange",
      link: "/returns"                  // ← new
    },
    {
      icon: "🔒",
      label: "Secure Payment",
      sub: "100% protected",
      // no link – stays as plain div
    },
  ].map(b =>
    b.link ? (
      <Link
        key={b.label}
        to={b.link}
        className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 flex-1 min-w-[120px] hover:bg-stone-100 transition-colors"
      >
        <span className="text-lg">{b.icon}</span>
        <div>
          <p className="text-xs font-bold text-stone-700 leading-none">{b.label}</p>
          <p className="text-[10px] text-stone-400">{b.sub}</p>
        </div>
      </Link>
    ) : (
      <div key={b.label} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 flex-1 min-w-[120px]">
        <span className="text-lg">{b.icon}</span>
        <div>
          <p className="text-xs font-bold text-stone-700 leading-none">{b.label}</p>
          <p className="text-[10px] text-stone-400">{b.sub}</p>
        </div>
      </div>
    )
  )}
</div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-stone-900">You may also like</h2>
                <button onClick={() => navigate("/shop")} className="text-sm text-rose-500 hover:text-rose-700 font-semibold transition-colors">
                  View all →
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {related.map(p => <RelatedCard key={p.id} product={p} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}